import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * A shared, singleton hero-photos store.
 *
 * Both the HeroSlideshow and the ManifestoShowreel subscribe to this
 * one store, so they *always* display the same current photograph.
 * That guarantees the visual crossfade between the Hero and the
 * Manifesto is on the same image (just different framings / overlays)
 * — which is what makes the transition feel like one continuous take
 * rather than two separate sections cutting to each other.
 *
 * Implementation: a module-scoped cache + a Set of subscriber setters.
 * The autoplay timer, API refresh timer, and visibility handling all
 * live at the module level so they run exactly once for the whole app.
 */

const AUTO_ADVANCE_MS = 6500;
const REFRESH_INTERVAL_MS = 60000;

let cache = { photos: [], current: 0, loaded: false };
const subscribers = new Set();
let refreshPromise = null;
let refreshId = null;
let advanceId = null;
let initialised = false;

function notify() {
  subscribers.forEach((setter) => setter(cache));
}

async function refresh() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      let { data: list } = await api.get("/photos", { params: { featured: true } });
      if (!Array.isArray(list) || list.length === 0) {
        const all = await api.get("/photos");
        list = Array.isArray(all.data) ? all.data : [];
      }
      const changed =
        list.length !== cache.photos.length ||
        list.some((p, i) => cache.photos[i]?.id !== p.id || cache.photos[i]?.image_url !== p.image_url);
      if (changed || !cache.loaded) {
        cache = {
          photos: list,
          current: cache.current >= list.length ? 0 : cache.current,
          loaded: true,
        };
        notify();
      }
    } catch {
      // Even on error, mark as loaded so we can show the empty backdrop.
      if (!cache.loaded) {
        cache = { ...cache, loaded: true };
        notify();
      }
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

function startTimers() {
  if (!advanceId) {
    advanceId = setInterval(() => {
      if (cache.photos.length < 2) return;
      cache = { ...cache, current: (cache.current + 1) % cache.photos.length };
      notify();
    }, AUTO_ADVANCE_MS);
  }
  if (!refreshId) {
    refreshId = setInterval(refresh, REFRESH_INTERVAL_MS);
  }
}

function stopTimers() {
  if (advanceId) { clearInterval(advanceId); advanceId = null; }
  if (refreshId) { clearInterval(refreshId); refreshId = null; }
}

function boot() {
  if (initialised || typeof window === "undefined") return;
  initialised = true;
  refresh();
  startTimers();
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stopTimers();
    else { refresh(); startTimers(); }
  });
}

export function useSharedHeroPhotos() {
  boot();
  const [state, setState] = useState(cache);

  useEffect(() => {
    subscribers.add(setState);
    // Ensure a subscriber joining late still gets the freshest state.
    setState(cache);
    return () => subscribers.delete(setState);
  }, []);

  const goTo = useCallback((i) => {
    if (cache.photos.length === 0) return;
    const total = cache.photos.length;
    const next = ((i % total) + total) % total;
    cache = { ...cache, current: next };
    notify();
  }, []);

  const nextSlide = useCallback(() => goTo(cache.current + 1), [goTo]);
  const prevSlide = useCallback(() => goTo(cache.current - 1), [goTo]);

  return {
    photos: state.photos,
    current: state.current,
    loaded: state.loaded,
    goTo,
    next: nextSlide,
    prev: prevSlide,
  };
}
